// The map buttons and the VIEW panel (R1747; card 08 of mockup round 082;
// cards 09 b, 10, 11 and 15 of round 086, R1805, R1806;
// ui-map-interface.md §12.0).
//
// The round VIEW button stands at the right edge of the map on a telephone,
// and on top of the chart strip at the strip's outer edge on a desktop
// (R2904). A press on VIEW opens the VIEW panel, the home of
// everything that stands over the map:
//
// - the CHART reading (off, historical, modern) as a pill group that writes
//   the setting OPTIONS · MAP writes (round 086 card 15, R1812, R2011);
// - the LAYERS: one tile per unit (chat, minimap, heat, legend), in the
//   look of the QUICK row of ☸ OPTIONS (`ui/options/kit.ts` `quickTile`)
//   and in the line icons of the kit, no glyph tile;
// - the CHART PIECES: TRACKS and WRECKS as ticked pills, each a toggle
//   of the pieces `[chart.groups]` lists (R1812, R2011, R2832, R3243);
// - the HEAT row, which opens the heat page of the panel: OFF first, then
//   one preview tile per mode (round 086 card 10, R2011);
// - on a telephone, the FACTION KEY section while the LEGEND tile is on
//   (card 09 b, R2832), because a telephone keeps no card on the glass at rest. It
//   folds to its head row by default, and a press on the head opens it,
//   so the main page does not scroll (R1859);
// - on a telephone, the DEPTH and the CURRENT sections of v2 phase 2 while
//   the LEGEND tile is on, the hello carries their field and the group
//   shows (R2616, R2832), under one
//   fold head that folds by default, as the key section does;
// - the action row GO TO THE HOLD (R1758). No foot line stands under it
//   (R1850).
// - the action row SEA VIEW HERE under it (R2493, R3111): it opens the
//   sea view at the selected fleet, which the sea view then follows, or
//   at the centre of the screen.
//
// Pure markup builders: the overlay reads the settings and the state, hands
// in what each part shows, and gets strings back, so a test pins the markup
// with no layout engine.
//
// A signal reads by shape as well as by colour: an open unit lights its
// tile and its pill reads ON, the active heat mode wears the accent and a
// check, the open panel puts an accent bar under the VIEW button, and a
// mode that paints puts the heat pip on VIEW (card 11). No gold.

import { KEYS } from '../gen/presentation';
import { CHART_READINGS, shownGroups, type ChartGroup, type ChartSetting } from './settings';
import { ACT as OPT_ACT } from './options/rows';
import { pillGroup, pillMulti, previewTiles, tag } from './choice/choice';
import { viewpanel as S } from '../gen/strings';
import { HeatmapKind } from '../gen/proto/aquaphobia';
import { claimLabel } from '../keys';
import { HEAT_MODES, heatMode, heatModeShown } from '../state';
import type { FeatureFlag } from '../gen/featureflags';
import { esc } from './esc';
import { lineIcon } from './lineicon';
import { rampCss } from './format';
import { hex } from '../world';

/** What the VIEW panel shows. The overlay reads it off the settings and the
 *  state, and the panel holds no state of its own. */
export interface ViewTiles {
  /** The chat box is open. */
  chat: boolean;
  /** The unread chat lines. */
  unread: number;
  /** The chart is open. */
  map: boolean;
  /** The heat mode that paints the map, NONE while none paints. */
  heat: HeatmapKind;
  /** `GameState.hasFeatureFlag`: the heat page lists a mode under a flag
   *  only while the world latched it (R2870). Left out, no flag is on. */
  has?: (flag: FeatureFlag) => boolean;
  /** The LEGEND tile of this form factor is on (R2832): the legend window
   *  of a desktop, the key and the DEPTH and CURRENT sections of a
   *  telephone. Null while the tile turns nothing on: a telephone whose
   *  World theme draws no key and whose world carries no deep field
   *  (R1568). */
  key: boolean | null;
  /** The World theme draws the faction key (R1568), so the FACTION KEY
   *  section of a telephone stands while the tile is on. Left out, true. */
  factions?: boolean;
  /** The key rows of a telephone (`faction-key.ts` `keyRowsHtml`), or ''
   *  on a desktop, where the key is a section of the legend card, and
   *  while the section is folded. */
  keyRows: string;
  /** The key section of a telephone is open. It folds to its head row by
   *  default, and the fold lasts the session (R1859). */
  keyOpen: boolean;
  /** The heat page of the panel stands in place of the main page. */
  heatPage: boolean;
  /** The sheet form factor: the key is a section of this panel there. */
  sheet: boolean;
  /** The chart reading, the setting OPTIONS · MAP writes too (R1812). */
  chart: ChartSetting;
  /** The groups of chart pieces the reader hid (R1812). */
  hide: readonly ChartGroup[];
  /** The groups the panel lists: a group under a flag stands only while
   *  the flag is on (`shownGroups`, R2616). Left out, the groups with no
   *  flag. */
  groups?: readonly ChartGroup[];
  /** The DEPTH and the CURRENT sections of a telephone (R2616): the fold
   *  head and the markup of `ui/chart/deep-legend.ts`, or null on a
   *  desktop and while neither field stands. The body is '' while the
   *  section is folded. */
  deep?: { title: string; body: string } | null;
  /** The DEPTH and CURRENT fold of a telephone is open (R2616). */
  deepOpen?: boolean;
  /** The SEA VIEW row (R2493): `off` while the world turns `seaview` off
   *  or the page opens no sea view, `follow` while a fleet is selected,
   *  else `here`. Left out, it is `off`. */
  sea?: SeaRow;
}

/** What the SEA VIEW row says, or `off` for no row. */
export type SeaRow = 'off' | 'here' | 'follow';

/** The line icons of the kit (mockup rounds 082 to 086), drawn in the
 *  stroke of the session buttons. */
export const ICONS = {
  layers: '<path d="M12 3.5l9 4.6-9 4.6-9-4.6z"/><path d="M3 12.4l9 4.6 9-4.6"/><path d="M3 16.6l9 4.6 9-4.6"/>',
  anchor: '<circle cx="12" cy="5.5" r="2"/><path d="M12 7.5V20M8 10.5h8"/><path d="M5 14c.3 3.6 3.2 6 7 6s6.7-2.4 7-6"/>',
  chat: '<path d="M4 5.5h16v10.5H10l-4.5 3.5V16H4z"/>',
  map: '<path d="M3.5 6.5l5.5-2 6 2 5.5-2v13l-5.5 2-6-2-5.5 2z"/><path d="M9 4.5v13M15 6.5v13"/>',
  heat: '<circle cx="12" cy="12" r="2.6"/><circle cx="12" cy="12" r="6" opacity=".7"/><circle cx="12" cy="12" r="9.4" opacity=".4"/>',
  key: '<path d="M7 4.5l3 3.5-3 3.5-3-3.5z"/><path d="M13 8h7M4.5 15.5h15.5M4.5 19.5h11"/>',
  back: '<path d="M14.5 5.5L8 12l6.5 6.5"/>',
  next: '<path d="M9.5 5.5L16 12l-6.5 6.5"/>',
  // The four of the VIEW panel of an arcade match (R1935): the score, the
  // sound on and off, the keys and the gestures.
  flag: '<path d="M6 21V4"/><path d="M6 4.5h11l-2.5 4 2.5 4H6"/>',
  sound: '<path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z"/><path d="M15.5 9a4.5 4.5 0 0 1 0 6M18 6.5a8 8 0 0 1 0 11"/>',
  mute: '<path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z"/><path d="M15.5 9.5l5 5M20.5 9.5l-5 5"/>',
  keys: '<rect x="3" y="6.5" width="18" height="11" rx="2"/><path d="M6.5 10h1M10 10h1M13.5 10h1M17 10h.5M8 14h8"/>',
  hand: '<path d="M9 11.5V5a1.5 1.5 0 0 1 3 0v6"/><path d="M12 10.5V9a1.5 1.5 0 0 1 3 0v2.5"/>'
    + '<path d="M15 11V10a1.5 1.5 0 0 1 3 0v4.5c0 3.5-2.5 6-6 6-2.6 0-4-1.2-5.4-3.2L4.3 14a1.4 1.4 0 0 1 2.2-1.7L9 15"/>',
  // The mark scale (R1992): a large hull chevron and a small one.
  marks: '<path d="M3.5 19.5L9 5l5.5 14.5L9 16z"/><path d="M15 19.5l3-7.5 3 7.5-3-1.8z"/>',
  // The sea view (R2493): a surface line, and an eye under it.
  sea: '<path d="M3 6.5q2.25-2 4.5 0t4.5 0 4.5 0 4.5 0"/><path d="M4 15.5q8-6 16 0q-8 6-16 0z"/><circle cx="12" cy="15.5" r="2"/>',
} as const;

/** One line icon. The stylesheet states its size, which follows the
 *  pointer as the button does; the attributes are the size before the
 *  sheet has loaded. */
export function icon(name: keyof typeof ICONS, cls = 'mic'): string {
  return lineIcon(cls, 18, ICONS[name]);
}

/** The light count of `n` unread chat lines, or '' for none. A count over
 *  99 reads 99+. The figure is for the eye: the control that carries it
 *  names the count for a screen reader. */
export function countHtml(n: number): string {
  if (n <= 0) return '';
  return `<span class="lcnt" aria-hidden="true">${n > 99 ? '99+' : n}</span>`;
}

/** The heat pip (round 086 card 11): the heat icon in the hue of the mode
 *  that paints, on VIEW, so the map never paints without a mark. '' while
 *  no mode paints. */
export function heatPipHtml(heat: HeatmapKind): string {
  if (heat === HeatmapKind.HEATMAP_KIND_NONE) return '';
  return `<span class="vw-pip" style="color:${hex(heatMode(heat).color)}" aria-hidden="true">`
    + `${icon('heat', 'pic')}</span>`;
}

/** The HEAT chip on the round unit (R1849, card 17 a of mockup round 086):
 *  the heat icon and HEAT in the on look, while a mode paints the disc.
 *  '' while no mode paints. The chip only reads; it takes no press. */
export function heatChipHtml(heat: HeatmapKind): string {
  if (heat === HeatmapKind.HEATMAP_KIND_NONE) return '';
  return `<span class="hchip" role="status" aria-label="${esc(S.heat_paints({ mode: heatMode(heat).label }))}">`
    + `${icon('heat', 'pic')}${esc(S.heat)}</span>`;
}

/** The inside of the VIEW button: the layers icon, the word VIEW, the
 *  light count of the unread chat lines and the heat pip. */
export function viewButtonHtml(count: number, heat: HeatmapKind = HeatmapKind.HEATMAP_KIND_NONE): string {
  return icon('layers') + `<span class="vw">${esc(S.view)}</span>` + countHtml(count) + heatPipHtml(heat);
}

/** The accessible name of the VIEW button: the word, the count and the
 *  mode that paints. */
export function viewButtonLabel(count: number, heat: HeatmapKind = HeatmapKind.HEATMAP_KIND_NONE): string {
  const parts = [S.view];
  if (count > 0) parts.push(S.unread({ n: count }));
  if (heat !== HeatmapKind.HEATMAP_KIND_NONE) parts.push(S.heat_paints({ mode: heatMode(heat).label }));
  return parts.join(' · ');
}

/** The ON or OFF mark of a switch tile: a tag, because it shows a state
 *  and takes no press; the tile is the press (R2011). The arcade VIEW
 *  panel draws it too (R1935). */
export function stateTag(on: boolean): string {
  return tag(on ? S.on : S.off, on ? 'accent' : 'plain');
}

/** One tile: the line icon and the state, the name, and the line under it.
 *  The arcade VIEW panel draws its tiles with a line (R1935); the LAYERS
 *  tiles of the map have none, and a tile with no line is short (R2564). */
export function tile(o: {
  icon: keyof typeof ICONS; name: string; on: boolean; act: string; state: string; sub?: string;
}): string {
  const sub = o.sub === undefined ? '' : `<i class="vp-sub">${esc(o.sub)}</i>`;
  return `<button type="button" class="o-qt${o.on ? ' on' : ''}${sub ? '' : ' bare'}" aria-pressed="${o.on}"`
    + ` data-act="${o.act}"><span class="o-qtop">${icon(o.icon, 'mic g')}`
    + `${o.state}</span><b>${esc(o.name)}</b>${sub}</button>`;
}

/** A section head of the panel: the word, and the line beside it. */
function shead(name: string, aside = ''): string {
  return `<div class="vp-sh"><b>${esc(name)}</b>${aside ? `<i>${esc(aside)}</i>` : ''}</div>`;
}

/**
 * The FACTION KEY section of a telephone (card 09 b, R1859). Its head row
 * is the fold control, as the section heads of the legend card are
 * (R1805): a chevron that points right while the section is folded and
 * down while it is open. The section folds by default, so the main page
 * stays inside the room over the VIEW button at UI scale 100.
 */
function keySectionHtml(open: boolean, rows: string): string {
  return foldHtml('viewkey', S.key, open)
    + (open && rows !== '' ? `<div class="vp-key-rows">${rows}</div>` : '');
}

/**
 * The DEPTH and the CURRENT sections of a telephone (R2616, round 088
 * cards 04 and 07): one fold head that names the sections that stand, in
 * the look of the key section, and their bodies while it is open.
 */
function deepSectionHtml(open: boolean, title: string, body: string): string {
  return foldHtml('viewdeep', title, open)
    + (open && body !== '' ? `<div class="vp-deep">${body}</div>` : '');
}

/** The fold head of a section of a telephone: the title, and a chevron
 *  that points right while the section is folded and down while it is
 *  open. */
function foldHtml(act: string, title: string, open: boolean): string {
  const chevron = open ? 'M6 9l6 6 6-6' : 'M9.5 5.5L16 12l-6.5 6.5';
  return `<button type="button" class="vp-sh vp-fold" data-act="${act}" aria-expanded="${open}">`
    + `<b>${esc(title)}</b><svg class="mic" viewBox="0 0 24 24" width="12" height="12" fill="none"`
    + ' stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">'
    + `<path d="${chevron}"/></svg></button>`;
}

/**
 * The action row under the sections (R1758): the anchor icon and GO TO THE
 * HOLD, and on a desktop the key that does the same. It carries the act of
 * the name in the dock head, `home`, and `data-from="view"` tells the
 * dispatcher to put the panel away. It is a row and not a tile: it acts
 * once and holds no state.
 */
export function homeRowHtml(touch: boolean): string {
  const key = touch ? '' : `<span class="vp-key">${esc(KEYS.map.home.label)}</span>`;
  return '<button type="button" class="vp-home" data-act="home" data-from="view">'
    + `${icon('anchor')}<b>${esc(S.home)}</b>${key}</button>`;
}

/**
 * The action row SEA VIEW HERE (R2493), under GO TO THE HOLD and in its
 * look. It leaves the map for the sea view of this world, and
 * `data-from="view"` puts the panel away first. With a fleet selected it
 * says that the sea view follows that fleet. It has no key.
 */
export function seaRowHtml(row: SeaRow): string {
  if (row === 'off') return '';
  return '<button type="button" class="vp-home vp-sea" data-act="seaview" data-from="view">'
    + `${icon('sea')}<b>${esc(row === 'follow' ? S.sea_follow : S.sea)}</b></button>`;
}

/** The four tiles of the LAYERS section, in their fixed order: CHAT,
 *  MINIMAP, HEAT, then LEGEND while it turns something on (R2832).
 *  No line stands under a name, so SEA VIEW HERE stands in the panel of a
 *  desktop with no scroll (R2564). */
function layerTiles(v: ViewTiles): string {
  const live = v.heat !== HeatmapKind.HEATMAP_KIND_NONE;
  const tiles = [
    tile({
      icon: 'chat', name: S.chat, on: v.chat, act: 'social',
      // A closed chat box shows its count; an open one shows that it is on.
      state: !v.chat && v.unread > 0 ? countHtml(v.unread) : stateTag(v.chat),
    }),
    tile({ icon: 'map', name: S.map, on: v.map, act: 'minimap', state: stateTag(v.map) }),
    // The HEAT tile is lit while a mode paints. A press turns the mode off,
    // or opens the heat page while none paints (R1806).
    tile({ icon: 'heat', name: S.heat, on: live, act: 'heat', state: stateTag(live) }),
  ];
  if (v.key !== null) {
    tiles.push(tile({ icon: 'key', name: S.legend, on: v.key, act: 'factionkey', state: stateTag(v.key) }));
  }
  return `<div class="o-qg vp-tiles">${tiles.join('')}</div>`;
}

/** The words of each chart reading of the switch. */
const READING_WORDS: Readonly<Record<ChartSetting, string>> = {
  off: S.reading_off, historical: S.reading_historical, modern: S.reading_modern,
};

/** The words of each group of chart pieces. */
const GROUP_WORDS: Readonly<Record<ChartGroup, string>> = {
  tracks: S.group_tracks, depth: S.group_depth,
  currents: S.group_currents, wrecks: S.group_wrecks,
};

/** The CHART section (round 086 card 15, R1812): the reading as a compact
 *  pill group of the choice kit (R2011). Each pill carries the act and the
 *  `data-id` of the chart row of OPTIONS · MAP, so one rule writes one
 *  setting. */
function chartSection(chart: ChartSetting): string {
  return shead(S.chart_head, S.chart_aside)
    + pillGroup(CHART_READINGS.map((id) => ({ id, label: READING_WORDS[id] })), chart, OPT_ACT.chart,
      { label: S.chart_head, size: 'compact' });
}

/** The CHART PIECES section (round 086 card 15, R1812): TRACKS,
 *  DEPTH and CURRENTS while their flags are on (R2616), and WRECKS, as the
 *  ticked pills of several values of the choice kit (R2011), each ticked
 *  while its pieces show. */
function piecesSection(hide: readonly ChartGroup[], groups: readonly ChartGroup[]): string {
  return shead(S.pieces_head)
    + '<div class="vp-pieces">'
    + pillMulti(groups.map((g) => ({ id: g, label: GROUP_WORDS[g] })),
      groups.filter((g) => !hide.includes(g)), 'chartgroup', { label: S.pieces_head, size: 'compact' })
    + '</div>';
}

/** The groups of chart pieces that stand under no flag. */
const UNFLAGGED = shownGroups(() => false);

/** The HEAT row of the main page: the mode that paints, and the way to the
 *  heat page (round 086 card 15). */
function heatRowHtml(heat: HeatmapKind): string {
  const name = heat === HeatmapKind.HEATMAP_KIND_NONE ? S.heat_off : heatMode(heat).label;
  return `<button type="button" class="vp-row" data-act="heatpage">${icon('heat')}`
    + `<b>${esc(S.heat_row({ mode: name }))}</b>${icon('next')}</button>`;
}

/**
 * The heat section of the panel (round 086 card 10): the modes as the
 * preview tiles of the choice kit (R2011), because each mode shows its
 * ramp: OFF first with a crossed swatch, then one tile per mode, two
 * columns. A press paints the mode, and a second press on it turns it off
 * (`actions.ts setHeatmap`). The active mode wears the accent rim and the
 * radio mark of a tile; a desktop shows the alt digit as a keycap in the
 * picture, a telephone none. The tiles have one size, so no tile and no
 * panel changes its width when the mark moves (R1869). One line under the
 * tiles names the mode and its ramp, at one height in every mode (R1850).
 */
export function heatSectionHtml(
  heat: HeatmapKind, touch: boolean, has: (flag: FeatureFlag) => boolean = () => false,
): string {
  // A mode under a flag the world did not latch is no tile (R2870). The
  // claim of mode i stays claim i, so the filter runs after the pairing.
  const tiles = HEAT_MODES.flatMap((m, i) => {
    if (!heatModeShown(m, has)) return [];
    const none = m.kind === HeatmapKind.HEATMAP_KIND_NONE;
    const swatch = none ? '<i class="vp-sw x"></i>' : `<i class="vp-sw" style="background:${rampCss(m)}"></i>`;
    const cap = touch ? '' : `<span class="vp-kc">${esc(claimLabel(KEYS.map.heat.claims[i]))}</span>`;
    return [{ id: String(m.kind), label: m.label, picture: `<span class="vp-tp" aria-hidden="true">${swatch}${cap}</span>` }];
  });
  const m = heatMode(heat);
  // The line under the tiles keeps one height in every mode (R1850): one
  // line of words, the ramp and its two ends. OFF keeps the ramp and the
  // ends as empty space, so the page does not move as a mode turns on.
  const what = heat === HeatmapKind.HEATMAP_KIND_NONE
    ? `<div class="vp-what">${esc(S.heat_none_what)}</div>`
      + '<div class="vp-ramp none" aria-hidden="true"></div>'
      + '<div class="vp-ends" aria-hidden="true"><span>&nbsp;</span></div>'
    : `<div class="vp-what" title="${esc(`${m.label} · ${m.what}`)}"><b>${esc(m.label)}</b> · ${esc(m.what)}</div>`
      + `<div class="vp-ramp" style="background:${rampCss(m)}"></div>`
      + `<div class="vp-ends"><span>${esc(m.lo)}</span><span>${esc(m.hi)}</span></div>`;
  return previewTiles(tiles, String(heat), 'heatset', { label: S.heat, size: 'compact', cols: 2 }) + what;
}

/**
 * The VIEW panel. The main page: the head, the CHART switch, the LAYERS
 * tiles, the CHART PIECES row, the HEAT row, the FACTION KEY and the DEPTH
 * and CURRENT sections of a telephone while the LEGEND tile is on (folded
 * by default), GO TO THE HOLD and, with the flag
 * `seaview` on, SEA VIEW HERE. The heat page: a head with
 * the way back and the heat section. Each tile carries the act of the unit
 * it toggles, so one dispatcher serves the panel, the minimize controls
 * and the keys. `touch` picks the keycaps and the aside of the heat page.
 */
export function viewPanelHtml(v: ViewTiles, touch: boolean): string {
  if (v.heatPage) {
    return '<div class="vp-h"><button type="button" class="vp-back" data-act="heatback"'
      + ` aria-label="${esc(S.back)}">${icon('back')}</button>`
      // A desktop shows the keys in the rows, so the head names none.
      + `<b>${esc(S.heat)}</b>${touch ? `<i>${esc(S.heat_aside_touch)}</i>` : ''}</div>`
      + heatSectionHtml(v.heat, touch, v.has);
  }
  // The LEGEND tile of a telephone shows the key and the deep sections
  // as one unit, as the tile of a desktop shows the legend window (R2832).
  const legend = v.sheet && v.key === true;
  const keySection = legend && (v.factions ?? true) ? keySectionHtml(v.keyOpen, v.keyRows) : '';
  return `<div class="vp-h"><b>${esc(S.head)}</b><i>${esc(S.head_sub)}</i></div>`
    + chartSection(v.chart)
    + shead(S.layers)
    + layerTiles(v)
    + piecesSection(v.hide, v.groups ?? UNFLAGGED)
    + heatRowHtml(v.heat)
    + keySection
    + (legend && v.deep ? deepSectionHtml(v.deepOpen ?? false, v.deep.title, v.deep.body) : '')
    + homeRowHtml(touch)
    + seaRowHtml(v.sea ?? 'off');
}
